import { useCallback, useMemo } from 'react';

import { useModal } from './useModal';
import { useQueryGetVenuesWithFilter } from './useQuery';

type Props = {
  venueName: string;
  venueAddress: string;
};

export const useCheckVenueExist = ({ venueName, venueAddress }: Props) => {
  const { isOpen, openModal, closeModal } = useModal();

  const {
    refetch: fetchVenueCheckByName,
    isFetching: isFetchingVenueCheckByName,
  } = useQueryGetVenuesWithFilter(
    {
      search: venueName,
      addressLine1: venueAddress,
      isFullNameMatch: true,
      page: 0,
      size: 1,
    },
    {
      enabled: false,
    },
  );

  const {
    refetch: fetchVenueCheckByAddress,
    isFetching: isFetchingVenueCheckByAddress,
  } = useQueryGetVenuesWithFilter(
    {
      addressLine1: venueAddress,
      page: 0,
      size: 1,
    },
    {
      enabled: false,
    },
  );

  const isFetchingVenueCheck = useMemo(() => {
    return isFetchingVenueCheckByName || isFetchingVenueCheckByAddress;
  }, [isFetchingVenueCheckByAddress, isFetchingVenueCheckByName]);

  const validateVenue = useCallback(
    (onContinue?: () => void) => {
      const fetchVenueFun = [fetchVenueCheckByName, fetchVenueCheckByAddress];

      Promise.all(fetchVenueFun.map((fn) => fn({ throwOnError: true })))
        .then(([nameResponse, addressResponse]) => {
          const hasExistedVenue =
            (nameResponse.data && nameResponse.data.count > 0) ||
            (addressResponse.data && addressResponse.data.count > 0);

          if (hasExistedVenue) {
            openModal();
          } else {
            onContinue && onContinue();
          }
        })
        .catch((err) => {
          // Skip validation on error to not block the flow
          // eslint-disable-next-line no-console
          console.error(err.message);
        });
    },
    [fetchVenueCheckByAddress, fetchVenueCheckByName, openModal],
  );

  return {
    isFetchingVenueCheck,
    validateVenue,
    isExistedVenueAlertOpen: isOpen,
    closeExistedVenueAlert: closeModal,
  };
};
